/**
 * AxisConfigurator
 *
 * Reusable component for configuring X and Y axes
 * Used by bar, line, area, scatter, and other axis-based charts
 */

import React, { useCallback } from 'react';
import { TextPropertyEditor } from '../../BaseEditors/TextPropertyEditor';
import { SelectPropertyEditor } from '../../BaseEditors/SelectPropertyEditor';
import type { ChartAxis } from './ChartBaseEditor';

export interface AxisConfiguratorProps {
  /** Current axes configuration */
  axes?: ChartAxis[];
  /** Called when axes change */
  onChange: (axes: ChartAxis[]) => void;
}

/**
 * AxisConfigurator component
 * Provides UI for configuring X and Y axes
 */
export const AxisConfigurator: React.FC<AxisConfiguratorProps> = ({
  axes = [],
  onChange,
}) => {
  // Get or create axis config for given type
  const getAxis = useCallback(
    (type: 'x' | 'y'): ChartAxis => {
      return axes.find((a) => a.type === type) || { type };
    },
    [axes],
  );

  // Update specific axis
  const updateAxis = useCallback(
    (type: 'x' | 'y', updates: Partial<ChartAxis>) => {
      const existing = getAxis(type);
      const updated = { ...existing, ...updates, type };
      const newAxes = axes.filter((a) => a.type !== type);
      onChange([...newAxes, updated]);
    },
    [axes, onChange, getAxis],
  );

  const xAxis = getAxis('x');
  const yAxis = getAxis('y');

  return (
    <div className="axis-configurator">
      {/* X-Axis Configuration */}
      <div className="axis-configurator__section">
        <h4 className="axis-configurator__subsection-title">X-Axis Configuration</h4>

        <TextPropertyEditor
          label="X-Axis Label"
          value={xAxis.label || ''}
          onChange={(label) => updateAxis('x', { label })}
          placeholder="e.g., Time, Category, Date"
          helpText="Label displayed for the X axis"
        />

        <div className="axis-configurator__checkbox">
          <input
            type="checkbox"
            id="x-show-grid"
            checked={xAxis.showGrid ?? false}
            onChange={(e) => updateAxis('x', { showGrid: e.target.checked })}
            className="af-property-editor__checkbox"
          />
          <label htmlFor="x-show-grid" className="af-property-editor__label">
            Show Grid Lines
          </label>
        </div>

        <SelectPropertyEditor
          label="X-Axis Scale Type"
          value={xAxis.scaleType || 'linear'}
          onChange={(scaleType) => updateAxis('x', { scaleType: scaleType as 'linear' | 'logarithmic' })}
          options={[
            { value: 'linear', label: 'Linear' },
            { value: 'logarithmic', label: 'Logarithmic' },
          ]}
          helpText="Linear: evenly spaced. Logarithmic: exponential spacing"
        />

        <div className="axis-configurator__number-input">
          <label className="af-property-editor__label">Tick Interval</label>
          <input
            type="number"
            min="1"
            max="1000"
            step="1"
            value={xAxis.tickInterval ?? 0}
            onChange={(e) => updateAxis('x', { tickInterval: Number(e.target.value) || undefined })}
            className="af-property-editor__input"
            placeholder="0 = auto"
          />
          <span className="af-property-editor__help">0 or blank = automatic spacing</span>
        </div>
      </div>

      {/* Y-Axis Configuration */}
      <div className="axis-configurator__section">
        <h4 className="axis-configurator__subsection-title">Y-Axis Configuration</h4>

        <TextPropertyEditor
          label="Y-Axis Label"
          value={yAxis.label || ''}
          onChange={(label) => updateAxis('y', { label })}
          placeholder="e.g., Revenue, Count, Amount"
          helpText="Label displayed for the Y axis"
        />

        <div className="axis-configurator__checkbox">
          <input
            type="checkbox"
            id="y-show-grid"
            checked={yAxis.showGrid ?? true}
            onChange={(e) => updateAxis('y', { showGrid: e.target.checked })}
            className="af-property-editor__checkbox"
          />
          <label htmlFor="y-show-grid" className="af-property-editor__label">
            Show Grid Lines
          </label>
        </div>

        <SelectPropertyEditor
          label="Y-Axis Scale Type"
          value={yAxis.scaleType || 'linear'}
          onChange={(scaleType) => updateAxis('y', { scaleType: scaleType as 'linear' | 'logarithmic' })}
          options={[
            { value: 'linear', label: 'Linear' },
            { value: 'logarithmic', label: 'Logarithmic' },
          ]}
          helpText="Linear: evenly spaced. Logarithmic: exponential spacing"
        />

        <div className="axis-configurator__number-input">
          <label className="af-property-editor__label">Tick Interval</label>
          <input
            type="number"
            min="1"
            max="1000"
            step="1"
            value={yAxis.tickInterval ?? 0}
            onChange={(e) => updateAxis('y', { tickInterval: Number(e.target.value) || undefined })}
            className="af-property-editor__input"
            placeholder="0 = auto"
          />
          <span className="af-property-editor__help">0 or blank = automatic spacing</span>
        </div>
      </div>
    </div>
  );
};

AxisConfigurator.displayName = 'AxisConfigurator';
